import { Formik, useFormikContext } from 'formik';

import { capitalize } from 'utils/string';
import { formatPrice } from 'utils/helpers';
import { clearExternalPaymentId } from 'utils/storage';
import { useVoidCloverPaymentMutation } from 'store/api/clover';
import {
  isMobile,
  useAlert,
  handleMutation,
  useSelectedCloverDevice,
} from 'hooks';
import {
  ButtonColors,
  PaymentMethod,
  CloverVoidReason,
  PaymentMethodType,
} from 'types';
import {
  Icon,
  Flex,
  List,
  Dialog,
  Button,
  ListCard,
  InfoBlock,
  ListHeader,
  DialogType,
  InputField,
  ButtonTypes,
  ErrorMessage,
  CommonStyles,
  DeleteButtonWithRef,
} from 'components';

import { Cash } from './cash';
import { Check } from './check';
import { Credit } from './credit';
import * as Styles from './styles';
import { PaymentMethodsProps } from './types';
import { ManualCredit } from './manual-credit';
import { initialValues, getValidationSchema } from './config';

const template = '1fr 1fr 24px';
const titles = ['Payment method', 'Amount'];

export const CheckoutPaymentMethods = <
  T extends { tip?: number; paymentMethods?: PaymentMethod[] },
>({
  company,
  eventData,
  saleTotal,
  checkoutId,
  setFormValues,
  onRemoveCredit,
  onAddCreditPaymentMethod,
}: PaymentMethodsProps<T>) => {
  const mobile = isMobile();
  const { showSuccessAlert } = useAlert();
  const { errors, values, touched } = useFormikContext<T>();
  const { selectedDeviceId: deviceId } = useSelectedCloverDevice(eventData);

  const [cloverVoid, mutation] = useVoidCloverPaymentMutation();
  handleMutation(mutation);

  const cards = [
    {
      disabled: true,
      icon: <Icon.Gift />,
      title: 'Gift card / Store credit',
    },
  ];

  const isProcessingFeeEnabled = company?.isProcessingFeeEnabled;
  const isManualCreditPaymentEnabled = eventData?.isManualCreditPaymentEnabled;

  const getFilteredPaymentMethods = (
    paymentMethods: PaymentMethod[] = [],
    method: PaymentMethod
  ) =>
    paymentMethods.filter((value) => {
      if (method.cloverPaymentId && value.cloverPaymentId) {
        return value.cloverPaymentId !== method.cloverPaymentId;
      }

      return value.id !== method.id;
    });

  const removeMethod = (method: PaymentMethod) =>
    setFormValues((prev) => ({
      ...prev,
      paymentMethods: getFilteredPaymentMethods(prev.paymentMethods, method),
    }));

  const onAddPaymentMethod = (method: PaymentMethod) =>
    setFormValues((prev) => ({
      ...prev,
      paymentMethods: [...(prev.paymentMethods ?? []), method],
    }));

  const onRemoveConfirm = async (
    item: PaymentMethod,
    approvalPassword: string,
    close: () => void
  ) => {
    console.log('[Checkout] start remove payment method:', {
      item,
      type: item.type,
      approvalPassword,
    });

    if (item.type !== PaymentMethodType.Credit) {
      removeMethod(item);
      close();
      return;
    }

    if (
      item.type === PaymentMethodType.Credit &&
      isManualCreditPaymentEnabled
    ) {
      removeMethod(item);
      close();
      return;
    }

    try {
      console.log('[Checkout] start void clover payment');
      const response = await cloverVoid({
        approvalPassword,
        id: item!.cloverPaymentId!,
        deviceId: Number(deviceId),
        eventId: Number(eventData?.id),
        voidReason: CloverVoidReason.UserCancel,
      }).unwrap();

      if (response?.id) {
        close();
        clearExternalPaymentId(String(eventData?.id));
        setFormValues((prev) => ({
          ...prev,
          tip: undefined,
          paymentMethods: getFilteredPaymentMethods(prev.paymentMethods, item),
        }));
        showSuccessAlert('Payment method successful voided');

        onRemoveCredit?.(response);

        console.log('[Checkout] finished void clover payment', { response });
      }
    } catch (error) {
      console.error(error);
      console.log('[Checkout] finished void clover payment with error', error);
    }
  };

  const commonPaymentProps = {
    amount: saleTotal,
    onAddPaymentMethod,
    isProcessingFeeEnabled,
  };

  return (
    <Flex gap={mobile ? 16 : 40} flexDirection="column">
      <Flex gap={16} flexDirection="column">
        {errors?.paymentMethods && touched?.paymentMethods && (
          <ErrorMessage>{errors.paymentMethods as string}</ErrorMessage>
        )}

        <Styles.Cards>
          <Cash {...commonPaymentProps} />

          {isManualCreditPaymentEnabled ? (
            <ManualCredit {...commonPaymentProps} />
          ) : (
            <Credit
              {...commonPaymentProps}
              event={eventData}
              checkoutId={checkoutId}
              onAddCreditPaymentMethod={onAddCreditPaymentMethod}
            />
          )}

          <Check {...commonPaymentProps} />
          {cards.map(({ icon, title, disabled }) => (
            <InfoBlock key={title} disabled={disabled}>
              <Styles.CardContainer>
                {icon}
                <h3>{title}</h3>
              </Styles.CardContainer>
            </InfoBlock>
          ))}
        </Styles.Cards>
      </Flex>

      {values?.paymentMethods?.length ? (
        <List
          mainWrapperStyles={{ padding: 0 }}
          count={values?.paymentMethods?.length}
          header={
            <ListHeader padding="8px" titles={titles} template={template} />
          }
        >
          {values?.paymentMethods?.map((item) => {
            const type = capitalize(item.type);
            const amount = formatPrice(item.totalAmount, '$0.00');
            const isCashPayment = item.type === PaymentMethodType.Cash;
            const isCreditCloverPayment =
              item.type === PaymentMethodType.Credit &&
              !isManualCreditPaymentEnabled;

            const receivedAmount = item.receivedCashAmount
              ? formatPrice(item.receivedCashAmount)
              : amount;

            return (
              <ListCard
                key={item.id}
                headers={titles}
                template={template}
                tabletTemplate={template}
                mobileTemplate={template}
                values={[
                  `${type} ${isCreditCloverPayment ? `Card ${item.last4CC}` : ''}`,
                  `${amount} ${isCashPayment ? `(Received ${receivedAmount})` : ''}`,
                  <Dialog
                    title="Delete payment method"
                    trigger={
                      <DeleteButtonWithRef disabled={mutation.isLoading} />
                    }
                  >
                    {(close, _, ButtonWrap) => (
                      <Formik
                        initialValues={initialValues}
                        validationSchema={getValidationSchema(
                          isCreditCloverPayment
                        )}
                        onSubmit={(values) =>
                          onRemoveConfirm(item, values.approvalPassword, close)
                        }
                      >
                        {({ isSubmitting }) => (
                          <CommonStyles.Form gap={24}>
                            <Flex column gap={16}>
                              <p>
                                You are going to delete payment method{' '}
                                <span className="semibold">{type}</span> with
                                amount{' '}
                                <span className="semibold">{amount}</span> Are
                                you sure you want to do that?
                              </p>

                              {isCreditCloverPayment && (
                                <InputField
                                  number
                                  autofill="off"
                                  label="Manager PIN"
                                  name="approvalPassword"
                                  placeholder="Manager PIN"
                                  autoComplete="one-time-code"
                                />
                              )}
                            </Flex>

                            <ButtonWrap type={DialogType.Plain}>
                              <Button
                                text="Close"
                                onClick={close}
                                colorType={ButtonColors.LightBlue}
                              />

                              <Button
                                text="Delete"
                                disabled={isSubmitting}
                                type={ButtonTypes.Submit}
                                colorType={ButtonColors.LightRed}
                              />
                            </ButtonWrap>
                          </CommonStyles.Form>
                        )}
                      </Formik>
                    )}
                  </Dialog>,
                ]}
              />
            );
          })}
        </List>
      ) : undefined}
    </Flex>
  );
};
